import React, { useState, useContext } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import Card from "../../shared/components/UIElements/Card";
import "./Auth.css";
import ErrorModal from "../../shared/components/UIElements/ErrorModal";
import LoadingSpinner from "../../shared/components/UIElements/LoadingSpinner";
import Input from "../../shared/components/FormElements/Input";
import Button from "../../shared/components/FormElements/Button";
import {
  VALIDATOR_EMAIL,
  VALIDATOR_MINLENGTH,
  VALIDATOR_REQUIRE,
} from "../../shared/util/validators";
import { useForm } from "../../shared/hooks/form-hook";
import { AuthContext } from "../../shared/context/auth-context";
import { useHttpClient } from "../../shared/hooks/http-hook";

const optionalLabel = { color: "#555", fontSize: "11px", marginLeft: "6px", fontWeight: 400 };

const Auth = () => {
  const auth = useContext(AuthContext);
  const navigate = useNavigate();
  // /auth?mode=signup opens the signup form; &admin=1 also shows the admin key field
  const [searchParams, setSearchParams] = useSearchParams();
  const showAdminKey = searchParams.get("admin") === "1";
  const [isLogin, setIsLogin] = useState(searchParams.get("mode") !== "signup");
  const [showPassword, setShowPassword] = useState(false);
  const { isLoading, error, sendRequest, clearError } = useHttpClient();

  const [formState, inputHandler, setFormData] = useForm(
    {
      ...(!isLogin && { name: { value: "", isValid: false } }),
      email:    { value: "", isValid: false },
      password: { value: "", isValid: false },
    },
    false
  );

  const authSubmitHandler = async (event) => {
    event.preventDefault();
    const { inputs } = formState;

    if (isLogin) {
      try {
        const responseData = await sendRequest(
          "/api/users/login",
          "POST",
          JSON.stringify({
            email:    inputs.email.value,
            password: inputs.password.value,
          }),
          { "Content-Type": "application/json" }
        );
        auth.login(responseData.userId, responseData.token, null, responseData.role, responseData.isPaid, responseData.paidUntil, responseData.name);
      } catch (err) {}
    } else {
      const authKey = showAdminKey && inputs.auth_key ? inputs.auth_key.value : "";
      try {
        const responseData = await sendRequest(
          "/api/users/signup",
          "POST",
          JSON.stringify({
            name:     inputs.name.value,
            email:    inputs.email.value,
            password: inputs.password.value,
            ...(authKey && { auth_key: authKey }),
          }),
          { "Content-Type": "application/json" }
        );
        auth.login(responseData.userId, responseData.token, null, responseData.role, responseData.isPaid, responseData.paidUntil, responseData.name);
        // Both state updates are batched, so the logged-in routes render
        // straight at the billing tab instead of redirecting /auth -> /.
        navigate("/usersettings?tab=billing", { replace: true, state: { justSignedUp: true } });
      } catch (err) {}
    }
  };

  const switchModeHandler = () => {
    // The password <Input> remounts empty on every switch (see its key), so
    // its entry is reset here too. The auth_key input re-registers itself on mount.
    setFormData(
      {
        ...formState.inputs,
        name:     isLogin ? { value: "", isValid: false } : undefined,
        password: { value: "", isValid: false },
        auth_key: undefined,
      },
      false
    );

    const params = new URLSearchParams(searchParams);
    if (isLogin) {
      params.set("mode", "signup");
    } else {
      params.delete("mode");
    }
    setSearchParams(params, { replace: true });
    setIsLogin((prevMode) => !prevMode);
  };

  return (
    <React.Fragment>
      <ErrorModal error={error} onClear={clearError} />
      <Card className="authentication">
        {isLoading && <LoadingSpinner asOverlay message="LOADING" />}
        <h2>{isLogin ? "Log in" : "Create your account"}</h2>
        <hr />
        <form onSubmit={authSubmitHandler}>
          {!isLogin && (
            <Input
              element="input"
              id="name"
              type="text"
              label="Name"
              autoComplete="name"
              validators={[VALIDATOR_REQUIRE()]}
              errorText="Please enter a name."
              onInput={inputHandler}
            />
          )}
          <Input
            element="input"
            id="email"
            type="email"
            label="E-Mail"
            autoComplete="email"
            validators={[VALIDATOR_EMAIL()]}
            errorText="Please enter a valid email address."
            onInput={inputHandler}
          />
          <Input
            key={isLogin ? "password-login" : "password-signup"}
            element="input"
            id="password"
            type={showPassword ? "text" : "password"}
            label="Password"
            autoComplete={isLogin ? "current-password" : "new-password"}
            validators={isLogin ? [VALIDATOR_REQUIRE()] : [VALIDATOR_MINLENGTH(8)]}
            errorText={isLogin ? "Please enter your password." : "Password must be at least 8 characters."}
            hint={isLogin ? undefined : "At least 8 characters."}
            onInput={inputHandler}
          />
          <label className="authentication__show-password">
            <input
              type="checkbox"
              checked={showPassword}
              onChange={(event) => setShowPassword(event.target.checked)}
            />
            Show password
          </label>
          {!isLogin && showAdminKey && (
            <Input
              element="input"
              id="auth_key"
              type="password"
              label={<>Admin key <span style={optionalLabel}>optional</span></>}
              autoComplete="off"
              validators={[]}
              errorText=""
              initialValid={true}
              onInput={inputHandler}
            />
          )}
          <Button type="submit" disabled={!formState.isValid}>
            {isLogin ? "LOG IN" : "CREATE ACCOUNT"}
          </Button>
        </form>
        <Button inverse onClick={switchModeHandler}>
          {isLogin ? "Don't have an account? Sign up" : "Already have an account? Log in"}
        </Button>
      </Card>
    </React.Fragment>
  );
};

export default Auth;
